import {
  ArrowLeftOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  HomeOutlined,
  QrcodeOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  Bubble,
  type Conversation,
  Conversations,
  type ConversationsProps,
} from '@ant-design/x';
import { history, useLocation, useModel, useRequest } from '@umijs/max';
import { App, Empty, Flex, type GetProp, Popover, QRCode, Spin } from 'antd';
import { useResponsive } from 'antd-style';
import cn from 'classnames';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { BASE_URL, getConfig } from '@/services';
import { getChatConfig, getConversations } from '@/services/chatbot';
import Chat from './chat';
import styles from './index.less';

const NEW_CONVERSATION_ID = 'new';

const QRCODE_URL = `${window.location.origin}/chatbot`;

interface ChatbotProps {
  show: boolean;
  onClose: () => void;
}
const Chatbot: React.FC<ChatbotProps> = ({ show, onClose }) => {
  const { message } = App.useApp();
  const { xs, sm, md, lg, xl, xxl } = useResponsive();
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const location = useLocation();

  // 判断是否在 chatbot 独立页面
  const isChatbotPage = location.pathname === '/chatbot';

  const user = useMemo(
    () => currentUser?.userId.toString() || '',
    [currentUser],
  );

  const [conversationId, setConversationId] = useState(NEW_CONVERSATION_ID);
  const [conversationsShow, setConversationsShow] = useState(false);

  const onCreateNewConversation = (id: string, name: string) => {
    setConversationId(id);
    mutateConversations((oldData) => [
      {
        key: id,
        label: name,
        group: '今天',
      },
      ...oldData,
    ]);
  };

  const conversationsMenuConfig: ConversationsProps['menu'] = (
    conversation,
  ) => ({
    items: [
      {
        label: '重命名',
        key: 'rename',
        icon: <EditOutlined />,
      },
      {
        label: '删除',
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
      },
    ],
    onClick: (menuInfo) => {
      console.log(menuInfo);
      menuInfo.domEvent.stopPropagation();
      // message.info(`Click ${conversation.key} - ${menuInfo.key}`);
    },
  });

  const { data: chatConfig } = useRequest(
    () => Promise.all([getConfig('robot_chatbot_name'), getChatConfig()]),
    {
      formatResult: (result) => ({
        ...result[1],
        bot_name: result[0].msg,
      }),
    },
  );

  const {
    run: fetchConversations,
    data: conversations,
    mutate: mutateConversations,
    loading: conversationsLoading,
  } = useRequest(
    () =>
      getConversations({
        user,
        limit: 20,
      }),
    {
      manual: true,
      formatResult: (result) =>
        result.data.map((item) => {
          const updateTime = dayjs.unix(item.updated_at);
          const isToday = updateTime.isSame(dayjs(), 'day');
          const isYesterday = updateTime.isSame(
            dayjs().subtract(1, 'day'),
            'day',
          );
          const group = isToday ? '今天' : isYesterday ? '昨天' : '更早以前';
          return {
            key: item.id,
            label: item.name,
            group,
          };
        }),
    },
  );

  useEffect(() => {
    if (conversationsShow && !conversations) {
      fetchConversations();
    }
  }, [conversationsShow]);

  useEffect(() => {
    if (!show) {
      setConversationId(NEW_CONVERSATION_ID);
      return;
    }

    // 阻止底层页面滚动
    // 保存原始的 overflow 值
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    // 获取滚动条宽度
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    // 锁定 body 滚动，并补偿滚动条消失导致的抖动
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      // 恢复原始样式
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [show]);

  const token = localStorage.getItem('token');

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="chatbot"
          className={styles['chatbot-container']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setConversationsShow(false)}
        >
          <header className={styles.header}>
            <div className={styles.left}>
              {isChatbotPage ? (
                <div
                  className={cn(styles.icon, styles['liquid-button'])}
                  onClick={() => {
                    onClose();
                    history.push('/dashboard');
                  }}
                >
                  <HomeOutlined />
                </div>
              ) : (
                <div
                  className={cn(styles.icon, styles['liquid-button'])}
                  onClick={onClose}
                >
                  <CloseOutlined />
                </div>
              )}
            </div>
            <h1>Chatbot</h1>
            <div className={styles.right}>
              {!isChatbotPage && md ? (
                <Popover
                  placement="bottom"
                  zIndex={3000}
                  content={
                    <QRCode
                      value={`${QRCODE_URL}?token=${token}`}
                      bordered={false}
                    />
                  }
                  arrow
                >
                  <div className={cn(styles.icon, styles['liquid-button'])}>
                    <QrcodeOutlined />
                  </div>
                </Popover>
              ) : null}
              <div
                className={cn(styles.button, styles['liquid-button'], {
                  [styles.active]: conversationsShow,
                  [styles.button]: md,
                  [styles.icon]: !md,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  setConversationsShow(!conversationsShow);
                }}
              >
                <HistoryOutlined />
                {md ? '历史对话' : ''}
              </div>
            </div>
          </header>
          <div className={styles.body}>
            {chatConfig ? (
              <Chat
                config={chatConfig}
                user={user}
                conversationId={conversationId}
                onCreateConversation={onCreateNewConversation}
              />
            ) : null}
            <AnimatePresence>
              {conversationsShow && (
                <motion.div
                  key="conversations"
                  className={styles.conversations}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: 200,
                    transition: { ease: 'easeOut', bounce: 0 },
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Spin spinning={conversationsLoading}>
                    {conversations && conversations.length > 0 ? (
                      <Conversations
                        // menu={conversationsMenuConfig}
                        items={[
                          {
                            key: NEW_CONVERSATION_ID,
                            label: '新会话',
                            group: '今天',
                          },
                          ...conversations,
                        ]}
                        className={styles['liquid-card']}
                        onActiveChange={(key) => {
                          setConversationId(key);
                          setConversationsShow(false);
                        }}
                        activeKey={conversationId}
                        groupable
                      />
                    ) : (
                      <div className={cn(styles['liquid-card'], styles.empty)}>
                        {!conversationsLoading && <Empty />}
                      </div>
                    )}
                  </Spin>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Chatbot;
